import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { getEnv, navigate } from '@/common/agents';
import { message } from '$/components/Message';
import { getFeaturedErrorText } from '$/domains/caseV3/featuredCaseMessages';
import {
  FeaturedErrorCode, FeaturedRequestError, getFeaturedPreview,
} from '$/domains/caseV3/featuredCaseService';
import { useOfficialStore } from '$/store/useOfficialStore';
import type { V3CanvasHomeFeaturedListData, V3CanvasHomeFeaturedListItem } from '$/domains/caseV3/type';
import { saveFeaturedQueue } from '$/domains/caseV3/featuredQueue';
import type { FeaturedCaseGalleryItem } from './index';
import { getFeaturedRouteQuery } from './routeQuery';

/** 精选详情与权限检查使用 featured_id 和真实投放上下文，画布预览再使用来源 case_id。 */
export function getFeaturedCaseQuery(item: FeaturedCaseGalleryItem) {
  return {
    featured_id: item.featuredId,
    placement: item.placement,
    region: item.region,
    locale: item.locale,
    category_id: item.categoryId,
  };
}

/** 列表点击公开工作流时按需预览；复制登录由只读画布处理。 */
export default function useFeaturedCaseActions() {
  const [creatingFeaturedId, setCreatingFeaturedId] = useState<string>();
  const lock = useRef(false);
  const generation = useRef(0);
  const listRef = useRef<V3CanvasHomeFeaturedListItem[]>([]);

  /** 记录本次实际显示的完整列表，供跨页恢复时保留首页投放顺序。 */
  const onLoaded = useCallback((data: V3CanvasHomeFeaturedListData, items: FeaturedCaseGalleryItem[]) => {
    const visibleIds = new Set(items.map((item) => item.featuredId));
    listRef.current = data.list.filter((item) => visibleIds.has(String(item.featured_id)));
  }, []);

  useEffect(() => () => { generation.current += 1; }, []);

  /** 打开既有 case_detail 路由；使首页尚未返回的预览检查结果失效。 */
  const openCase = useCallback((item: FeaturedCaseGalleryItem) => {
    generation.current += 1;
    saveFeaturedQueue(getFeaturedCaseQuery(item), listRef.current, item.featuredId);
    navigate({
      mode: 'url',
      url: getEnv().ENV === 'development' ? '/--roboneo/case_detail' : '/case_detail',
      query: getFeaturedRouteQuery(item.featuredId, item.placement),
    });
  }, []);

  /** 访客也可在权限校验后进入只读预览，不调用复制或创建项目接口。 */
  const createSame = useCallback(async (item: FeaturedCaseGalleryItem) => {
    if (lock.current || item.workflowPublic !== true) return;
    lock.current = true;
    setCreatingFeaturedId(item.featuredId);
    const actionGeneration = generation.current;
    const actionUid = useOfficialStore.getState().userInfo?.uid;

    /** 页面离开、详情打开或账号切换后，旧操作不得继续创建或导航。 */
    const isCurrentAction = () => generation.current === actionGeneration
      && useOfficialStore.getState().userInfo?.uid === actionUid;

    try {
      const detail = await getFeaturedPreview(getFeaturedCaseQuery(item));
      if (!isCurrentAction()) return;
      if (String(detail.featured_id) !== item.featuredId || !detail.case) {
        throw new FeaturedRequestError(FeaturedErrorCode.Unavailable);
      }
      if (detail.presentation?.workflow_public !== true) {
        throw new FeaturedRequestError(FeaturedErrorCode.CopyForbidden);
      }
      const caseId = String(detail.case.case_id ?? detail.case_id ?? '');
      if (!/^[1-9]\d*$/.test(caseId)) {
        throw new FeaturedRequestError(FeaturedErrorCode.Unavailable);
      }
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/case_preview' : '/case_preview',
        query: getFeaturedRouteQuery(item.featuredId, item.placement),
      });
    } catch (error) {
      if (isCurrentAction()) message.error(getFeaturedErrorText(error));
    } finally {
      lock.current = false;
      if (generation.current === actionGeneration) setCreatingFeaturedId(undefined);
    }
  }, []);

  return {
    openCase, createSame, creatingFeaturedId, onLoaded,
  };
}
